import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { 
  FaBriefcase, 
  FaRobot, 
  FaMobile, 
  FaVideo, 
  FaTools,
  FaPen, 
  FaCode,
  FaChevronRight,
  FaCalendarAlt
} from 'react-icons/fa';

const Experience = ({ currentTheme }) => {
  const experiences = [
    {
      title: "Intern",
      company: "APBC Africa",
      period: "May - October 2024",
      icon: <FaBriefcase />,
      color: "primary",
      achievements: [
        {
          text: "Designed and implemented a chatbot using OpenAI Key to automate customer service tasks",
          icon: <FaRobot />
        },
        {
          text: "Collaborated on a team to develop responsive websites and mobile applications",
          icon: <FaMobile />
        },
        {
          text: "Installed, configured, and maintained CCTV systems, ensuring optimal performance",
          icon: <FaVideo />
        },
        {
          text: "Provided comprehensive IT support, resolving hardware and software issues",
          icon: <FaTools />
        }
      ]
    },
    {
      title: "Technical Writer and Research Writer",
      company: "Remote",
      period: "2023 - 2024",
      icon: <FaPen />,
      color: "success",
      achievements: [
        {
          text: "Authored technical guides and articles focusing on emerging trends in software development and technology",
          icon: <FaPen />
        },
        {
          text: "Conducted detailed research, creating professional-grade reports tailored to specific client needs",
          icon: <FaCode />
        }
      ]
    },
    {
      title: "Software Development Projects",
      company: "Freelance & Academic",
      period: "2020 - 2024",
      icon: <FaCode />,
      color: "info",
      achievements: [
        {
          text: "Delivered numerous academic and freelance projects, focusing on front-end development using React",
          icon: <FaCode />
        },
        {
          text: "Contributed to the back-end implementation of projects, ensuring functionality and user experience",
          icon: <FaCode />
        },
        {
          text: "Successfully developed mobile-friendly websites and applications as part of academic and remote assignments",
          icon: <FaMobile />
        }
      ]
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <Container fluid className="py-5 position-relative overflow-hidden" id='experience' style={{ backgroundColor: currentTheme.background }}>
      <div className="position-absolute w-100 h-100 top-0 start-0" style={{ zIndex: -1 }}>
        <motion.div
          animate={{
            background: [
              `linear-gradient(45deg, ${currentTheme.primary}20 0%, ${currentTheme.secondary}20 100%)`,
              `linear-gradient(45deg, ${currentTheme.secondary}20 0%, ${currentTheme.primary}20 100%)`
            ],
          }}
          transition={{ duration: 3, repeat: Infinity, repeatType: 'reverse' }}
          className="w-100 h-100"
        />
      </div>

      <Container className="position-relative">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={containerVariants}
        >
          <Row className="justify-content-center mb-5">
            <Col lg={8} className="text-center">
              <motion.h2 
                className="display-4 fw-bold mb-4"
                variants={itemVariants}
                style={{ color: currentTheme.primary }}
              >
                WORK EXPERIENCE
              </motion.h2>
              <motion.div 
                className="w-25 mx-auto border-bottom border-3 mb-5"
                variants={itemVariants}
                style={{ borderColor: currentTheme.primary }}
              />
            </Col>
          </Row>

          <Row className="g-4">
            {experiences.map((exp, index) => (
              <Col lg={12} key={index}>
                <motion.div
                  variants={itemVariants}
                  className="card border-0 shadow-sm h-100 position-relative overflow-hidden"
                  whileHover={{ y: -5, transition: { duration: 0.3 } }}
                  style={{ backgroundColor: currentTheme.background === '#121212' ? '#1E1E1E' : '#FFFFFF' }}
                >
                  <div className="card-body p-4">
                    <Row className="align-items-center">
                      <Col lg={3}>
                        <motion.div 
                          className="d-flex align-items-center mb-3 mb-lg-0"
                          whileHover={{ scale: 1.05 }}
                        >
                          <div className="p-3 rounded-circle text-white me-3" style={{ backgroundColor: currentTheme.primary }}>
                            {exp.icon}
                          </div>
                          <div>
                            <h3 className="h5 mb-1" style={{ color: currentTheme.text }}>{exp.title}</h3>
                            <p className="mb-0" style={{ color: currentTheme.text }}>{exp.company}</p>
                            <small style={{ color: currentTheme.text }}>
                              <FaCalendarAlt className="me-1" />
                              {exp.period}
                            </small>
                          </div>
                        </motion.div>
                      </Col>
                      <Col lg={9}>
                        <div className="ps-lg-4">
                          {exp.achievements.map((achievement, idx) => (
                            <motion.div
                              key={idx}
                              className="d-flex align-items-center mb-2"
                              whileHover={{ x: 10 }}
                              transition={{ type: "spring", stiffness: 300 }}
                            >
                              <FaChevronRight style={{ color: currentTheme.primary }} className="me-2" />
                              <div className="d-flex align-items-center">
                                <div style={{ color: currentTheme.primary }} className="me-2">
                                  {achievement.icon}
                                </div>
                                <p className="mb-0" style={{ color: currentTheme.text }}>{achievement.text}</p>
                              </div>
                            </motion.div>
                          ))}
                        </div>
                      </Col>
                    </Row>
                  </div>
                  <motion.div
                    className="position-absolute top-0 start-0 w-100 h-100"
                    initial={{ opacity: 0 }}
                    whileHover={{ opacity: 0.05 }}
                    style={{ backgroundColor: currentTheme.primary }}
                  />
                </motion.div>
              </Col>
            ))}
          </Row>
        </motion.div>

        <motion.div
          animate={{
            rotate: 360,
            transition: { duration: 20, repeat: Infinity, ease: "linear" }
          }}
          className="position-absolute"
          style={{ top: '10%', right: '5%', opacity: 0.1 }}
        >
          <FaCode style={{ fontSize: '4rem', color: currentTheme.primary }} />
        </motion.div>
      </Container>
    </Container>
  );
};

export default Experience;